import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useCampaign } from '../../contexts/CampaignContext';
import { Info } from 'lucide-react';

const CampaignForm = ({ isEdit = false }) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const {
        createCampaign,
        updateCampaign,
        fetchCampaign,
        fetchTemplates,
        testTemplate,
        loading,
        error,
        currentCampaign
    } = useCampaign();

    // Form state
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        templateId: '',
        templateName: '',
        templateLanguage: '',
        templateComponents: [],
        templateVariables: [],
        settings: {
            batchSize: 50,
            delayBetweenMessages: 1000,
            delayBetweenBatches: 60000
        }
    });

    // Templates list
    const [templates, setTemplates] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [previewData, setPreviewData] = useState(null);
    const [testVariables, setTestVariables] = useState({});
    const [mediaHeaders, setMediaHeaders] = useState({
        hasMedia: false,
        type: null,
        url: ''
    });

    // Load campaign data if editing
    useEffect(() => {
        const loadData = async () => {
            // Load templates
            const templatesResponse = await fetchTemplates();
            if (templatesResponse && templatesResponse.data && templatesResponse.data.data) {
                // Extract the actual array of templates from the nested structure
                setTemplates(templatesResponse.data.data);
            } else {
                setTemplates([]);
            }

            // If editing, load campaign data
            if (isEdit && id) {
                const campaign = await fetchCampaign(id);
                if (campaign) {
                    setFormData({
                        name: campaign.name || '',
                        description: campaign.description || '',
                        templateId: campaign.templateId || '',
                        templateName: campaign.templateName || '',
                        templateLanguage: campaign.templateLanguage || '',
                        templateComponents: campaign.templateComponents || [],
                        templateVariables: campaign.templateVariables || [],
                        settings: {
                            batchSize: campaign.settings?.batchSize || 50,
                            delayBetweenMessages: campaign.settings?.delayBetweenMessages || 1000,
                            delayBetweenBatches: campaign.settings?.delayBetweenBatches || 60000
                        }
                    });

                    // Find the selected template if we have templates
                    if (templatesResponse && templatesResponse.data && templatesResponse.data.data) {
                        const template = templatesResponse.data.data.find(t => t.name === campaign.templateName);
                        if (template) {
                            setSelectedTemplate(template);

                            // Extract variables from template components
                            extractTemplateVariables(campaign.templateComponents);
                        }
                    }
                }
            }
        };

        loadData();
    }, [id, isEdit, fetchCampaign, fetchTemplates]);

    // Handle template selection
    const handleTemplateSelect = async (e) => {
        const templateName = e.target.value;
        const template = templates.find(t => t.name === templateName);

        if (template) {
            setSelectedTemplate(template);

            // Check for media headers early
            let hasMediaHeader = false;
            let mediaType = null;

            if (template.components) {
                const headerComponent = template.components.find(c => c.type === 'HEADER');
                if (headerComponent && headerComponent.format &&
                    ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(headerComponent.format)) {
                    hasMediaHeader = true;
                    mediaType = headerComponent.format.toLowerCase();
                }
            }

            setMediaHeaders({
                hasMedia: hasMediaHeader,
                type: mediaType,
                url: ''
            });

            // Map template components to the format expected by the WhatsApp API
            // Following the format in https://developers.facebook.com/docs/whatsapp/cloud-api/guides/send-message-templates/
            const mappedComponents = template.components.map(comp => {
                // Base component object with proper type (uppercase for WhatsApp API)
                const component = {
                    type: comp.type  // Keep original uppercase type
                };

                // Add parameters based on component type and whether it has dynamic variables
                if (comp.type === 'BODY' || comp.type === 'FOOTER') {
                    if (comp.text) {
                        // Check if the text contains variables like {{1}}
                        const hasVariables = comp.text.match(/{{([^}]+)}}/g);
                        if (hasVariables) {
                            component.parameters = [{
                                type: 'text',
                                text: comp.text
                            }];
                        }
                    }
                } else if (comp.type === 'HEADER') {
                    // For header, check if it has a format property and what type it is
                    if (comp.format) {
                        if (comp.format === 'TEXT' && comp.text) {
                            // Only add parameters if the header text contains variables
                            const hasVariables = comp.text.match(/{{([^}]+)}}/g);
                            if (hasVariables && hasVariables.length > 0) {
                                component.parameters = [{
                                    type: 'text',
                                    text: comp.text
                                }];
                            }
                            // Do not add parameters for static text headers
                        } else if (['IMAGE', 'VIDEO', 'DOCUMENT'].includes(comp.format)) {
                            // Media headers always need parameters
                            component.parameters = [{
                                type: comp.format.toLowerCase(),
                                [comp.format.toLowerCase()]: {
                                    link: ''
                                }
                            }];
                        }
                    }
                } else if (comp.type === 'BUTTONS' && comp.buttons) {
                    // Handle buttons according to WhatsApp API format
                    component.buttons = comp.buttons.map(btn => ({
                        type: btn.type, // QUICK_REPLY, URL, PHONE_NUMBER, etc.
                        text: btn.text
                    }));
                }

                return component;
            });

            // Extract template variables with descriptions for storage in the campaign
            const templateVariables = [];
            const variablesMap = {};

            // Process each component to extract variables
            template.components.forEach(comp => {
                if (comp.text) {
                    // Extract {{variable}} patterns with index
                    const matches = comp.text.match(/{{([^}]+)}}/g) || [];
                    matches.forEach((match, index) => {
                        const varName = match.slice(2, -2).trim();

                        // Only add unique variables
                        if (!variablesMap[varName]) {
                            variablesMap[varName] = true;

                            // Get example value from template examples if available
                            let example = '';
                            if (comp.type === 'HEADER' && comp.example?.header_text?.[index]) {
                                example = comp.example.header_text[index];
                            } else if (comp.type === 'BODY' && comp.example?.body_text?.[0]?.[index]) {
                                example = comp.example.body_text[0][index];
                            }

                            // Create a descriptive variable object
                            templateVariables.push({
                                name: varName,
                                description: `${comp.type.toLowerCase()} parameter ${varName}`,
                                example: example || varName,
                                componentType: comp.type
                            });
                        }
                    });
                }
            });

            // Update form data with template info
            setFormData(prev => ({
                ...prev,
                templateId: template.id,
                templateName: template.name,
                templateLanguage: template.language,
                templateComponents: mappedComponents,
                templateVariables: templateVariables // Store template variables
            }));

            // Extract variables from components for testing
            const variables = {};
            template.components.forEach(comp => {
                if (comp.text) {
                    // Extract {{variable}} patterns
                    const matches = comp.text.match(/{{([^}]+)}}/g) || [];
                    matches.forEach(match => {
                        const varName = match.slice(2, -2).trim();
                        variables[varName] = '';
                    });
                }
            });

            setTestVariables(variables);
            setPreviewData(null);
        } else {
            setSelectedTemplate(null);
            setFormData(prev => ({
                ...prev,
                templateId: '',
                templateName: '',
                templateLanguage: '',
                templateComponents: [],
                templateVariables: [] // Clear template variables
            }));
            setTestVariables({});
            setPreviewData(null);
        }
    };

    // Extract variables from template components for testing
    const extractTemplateVariables = (components) => {
        const variables = {};

        if (!components) return;

        components.forEach(component => {
            if (component.parameters) {
                component.parameters.forEach(param => {
                    if (param.type === 'text') {
                        // Extract variable names enclosed in {{ }}
                        const matches = param.text.match(/{{([^}]+)}}/g) || [];
                        matches.forEach(match => {
                            const varName = match.slice(2, -2).trim();
                            variables[varName] = '';
                        });
                    }
                });
            }
        });

        setTestVariables(variables);
    };

    // Handle form input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;

        if (name.startsWith('settings.')) {
            const settingName = name.split('.')[1];
            setFormData(prev => ({
                ...prev,
                settings: {
                    ...prev.settings,
                    [settingName]: Number(value)
                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    // Handle test variable changes
    const handleVariableChange = (e) => {
        const { name, value } = e.target;
        setTestVariables(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Handle media header URL change
    const handleMediaHeaderChange = (url) => {
        setMediaHeaders(prev => ({
            ...prev,
            url
        }));
    };

    // Test template rendering
    const handleTestTemplate = async () => {
        if (!formData.templateComponents.length) return;

        try {
            // Create a copy of template components for testing
            const testComponents = formData.templateComponents.map(component => {
                // Clone the component
                const testComponent = { ...component };

                // For media headers, add the URL from mediaHeaders state
                if (testComponent.type === 'HEADER' && testComponent.parameters &&
                    mediaHeaders.hasMedia && mediaHeaders.type && mediaHeaders.url) {
                    // Deep clone parameters to avoid mutation
                    testComponent.parameters = testComponent.parameters.map(param => {
                        if (param.type === mediaHeaders.type) {
                            return {
                                ...param,
                                [mediaHeaders.type]: {
                                    link: mediaHeaders.url
                                }
                            };
                        }
                        return param;
                    });
                }

                // If the component has text parameters, populate them with test values
                else if (component.parameters) {
                    testComponent.parameters = component.parameters.map(param => {
                        if (param.type === 'text' && param.text) {
                            // Create a new parameter with replaced variables
                            const newParam = { ...param };
                            // Replace all {{var}} placeholders with actual test values
                            const text = param.text.replace(/{{([^}]+)}}/g, (match, varName) => {
                                return testVariables[varName] || match;
                            });
                            newParam.text = text;
                            return newParam;
                        }
                        return param;
                    });
                }

                return testComponent;
            });

            const processedComponents = await testTemplate({
                components: testComponents,
                variables: testVariables
            });

            if (processedComponents) {
                // Process template result
                const processedResult = processedComponents.map(component => {
                    // Keep the original structure from API response
                    let result = { ...component };

                    // Ensure type is preserved in the original case from API
                    // WhatsApp API uses uppercase types (HEADER, BODY, BUTTONS, etc.)

                    // Special handling for button components
                    if (component.type === 'BUTTONS' || component.type === 'buttons') {
                        // Normalize type to uppercase for consistency
                        result.type = 'BUTTONS';

                        // If buttons already exist in the proper format, keep them
                        if (component.buttons && Array.isArray(component.buttons)) {
                            result.buttons = component.buttons;
                        }
                        // Extract button info from parameters if present in that format
                        else if (component.parameters && component.parameters.length > 0) {
                            const buttonParams = component.parameters.filter(param => param.type === 'button');

                            if (buttonParams.length > 0) {
                                result.buttons = buttonParams.map(param => ({
                                    type: param.sub_type?.toUpperCase() || 'QUICK_REPLY',
                                    text: param.text
                                }));
                            }
                            // Fallback to selected template if available
                            else if (selectedTemplate) {
                                const buttonComp = selectedTemplate.components.find(c =>
                                    c.type === 'BUTTONS' && c.buttons && c.buttons.length > 0);

                                if (buttonComp && buttonComp.buttons) {
                                    result.buttons = buttonComp.buttons;
                                }
                            }
                        }
                    }

                    return result;
                });

                // Update preview data
                setPreviewData({
                    templateName: formData.templateName,
                    processedComponents: processedResult
                });
            }
        } catch (err) {
            console.error('Template test error:', err);
        }
    };

    // Submit form
    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            if (isEdit && id) {
                await updateCampaign(id, formData);
                navigate(`/campaigns/${id}`);
            } else {
                const campaign = await createCampaign(formData);
                if (campaign && campaign._id) {
                    navigate(`/campaigns/${campaign._id}`);
                }
            }
        } catch (err) {
            console.error('Error submitting campaign:', err);
        }
    };

    // Cancel form
    const handleCancel = () => {
        if (isEdit && id) {
            navigate(`/campaigns/${id}`);
        } else {
            navigate('/campaigns');
        }
    };

    // Modify the renderTemplatePreview function to show all header types
    const renderTemplatePreview = () => {
        if (!previewData) {
            return null;
        }

        return (
            <div className="mt-4">
                <h4 className="text-sm font-medium mb-2">WhatsApp Message Preview</h4>
                {/* WhatsApp Preview UI - Similar to official WhatsApp Business templates */}
                <div className="max-w-md mx-auto border border-gray-200 rounded-lg overflow-hidden shadow-md">
                    {/* Header */}
                    <div className="p-3 bg-green-600 text-white flex items-center">
                        <div className="h-10 w-10 bg-white rounded-full flex items-center justify-center text-green-600 font-bold text-lg mr-3 shadow-sm">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
                                <path fillRule="evenodd" d="M4.804 21.644A6.707 6.707 0 006 21.75a6.721 6.721 0 003.583-1.029c.774.182 1.584.279 2.417.279 5.322 0 9.75-3.97 9.75-9 0-5.03-4.428-9-9.75-9s-9.75 3.97-9.75 9c0 2.409 1.025 4.587 2.674 6.192.232.226.277.428.254.543a3.73 3.73 0 01-.814 1.686.75.75 0 00.44 1.223zM8.25 10.875a1.125 1.125 0 100 2.25 1.125 1.125 0 000-2.25zM10.875 12a1.125 1.125 0 112.25 0 1.125 1.125 0 01-2.25 0zm4.875-1.125a1.125 1.125 0 100 2.25 1.125 1.125 0 000-2.25z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <div>
                            <div className="font-semibold">WhatsApp Business</div>
                            <div className="text-xs opacity-80">Template Preview</div>
                        </div>
                    </div>

                    {/* Message body */}
                    <div className="bg-white m-4 rounded-lg shadow-sm">
                        {/* Check if processedComponents exists before accessing it */}
                        {previewData.processedComponents && (
                            <div className="p-3 text-gray-800 whitespace-pre-line">
                                {/* Find and display header text or media */}
                                {previewData.processedComponents
                                    .filter(comp => comp.type === 'HEADER' || comp.type === 'header')
                                    .map((comp, i) => {
                                        // Check for media headers
                                        if (comp.parameters && comp.parameters.length > 0) {
                                            const param = comp.parameters[0];

                                            // Image header
                                            if (param.type === 'image' && param.image?.link) {
                                                return (
                                                    <div key={`header-${i}`} className="mb-3">
                                                        <img
                                                            src={param.image.link}
                                                            alt="Header"
                                                            className="w-full h-auto rounded mb-2"
                                                            onError={(e) => {
                                                                e.target.onerror = null;
                                                                e.target.src = 'https://placehold.co/600x400?text=Image+Preview';
                                                            }}
                                                        />
                                                    </div>
                                                );
                                            }

                                            // Document header
                                            else if (param.type === 'document' && param.document?.link) {
                                                return (
                                                    <div key={`header-${i}`} className="mb-3 p-3 bg-gray-100 rounded flex items-center">
                                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 mr-2">
                                                            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                                                        </svg>
                                                        <span className="text-sm">Document: {param.document.link.split('/').pop() || 'Attachment'}</span>
                                                    </div>
                                                );
                                            }

                                            // Video header
                                            else if (param.type === 'video' && param.video?.link) {
                                                return (
                                                    <div key={`header-${i}`} className="mb-3">
                                                        <div className="bg-gray-100 rounded p-3 flex items-center">
                                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 mr-2">
                                                                <path strokeLinecap="round" d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z" />
                                                            </svg>
                                                            <span className="text-sm">Video</span>
                                                        </div>
                                                    </div>
                                                );
                                            }

                                            // Text header with parameter
                                            else if (param.type === 'text' && param.text) {
                                                return (
                                                    <div key={`header-${i}`} className="font-bold mb-2">
                                                        {param.text}
                                                    </div>
                                                );
                                            }
                                        }

                                        // Static text header (no parameters)
                                        return (
                                            <div key={`header-${i}`} className="font-bold mb-2">
                                                {selectedTemplate && selectedTemplate.components ?
                                                    selectedTemplate.components.find(c => c.type === 'HEADER')?.text || "Header" :
                                                    "Header"
                                                }
                                            </div>
                                        );
                                    })}

                                {/* Find and display body text */}
                                {previewData.processedComponents
                                    .filter(comp => comp.type === 'BODY' || comp.type === 'body')
                                    .map((comp, i) => (
                                        <div key={`body-${i}`}>
                                            {comp.parameters && comp.parameters.length > 0 && comp.parameters[0].text ?
                                                comp.parameters[0].text :
                                                "Body text will appear here."}
                                        </div>
                                    ))}
                            </div>
                        )}

                        {/* Button section */}
                        {previewData.processedComponents && previewData.processedComponents.some(comp =>
                            comp.type === 'BUTTONS' ||
                            comp.type === 'buttons' ||
                            (comp.buttons && comp.buttons.length > 0)
                        ) && (
                                <div className="border-t border-gray-200">
                                    {/* Find and display buttons */}
                                    {previewData.processedComponents
                                        .filter(comp => comp.type === 'BUTTONS' || comp.type === 'buttons' || (comp.buttons && comp.buttons.length > 0))
                                        .map((comp, i) => (
                                            <div key={`buttons-${i}`} className="p-2">
                                                {comp.buttons && comp.buttons.map((btn, j) => (
                                                    <button
                                                        key={`btn-${j}`}
                                                        className="w-full p-2 mt-1 text-center border border-gray-300 rounded text-blue-600 hover:bg-gray-50"
                                                    >
                                                        {btn.text}
                                                    </button>
                                                ))}
                                            </div>
                                        ))}
                                </div>
                            )}
                    </div>

                    {/* Footer */}
                    <div className="px-4 py-2 bg-gray-50 text-xs text-gray-500 border-t border-gray-200">
                        Template: {previewData.templateName}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b">
                <h2 className="text-lg font-semibold">
                    {isEdit ? 'Edit Campaign' : 'Create New Campaign'}
                </h2>
            </div>

            {error && (
                <div className="p-4 bg-red-100 text-red-700 border-l-4 border-red-500">
                    {error}
                </div>
            )}

            <div className="mb-8 mt-6 mx-4  my-4 bg-blue-50 border border-blue-200 rounded-lg p-5">
                <div className="flex items-start">
                    <div className="flex-shrink-0">
                        <Info className="h-5 w-5 text-blue-500 mt-0.5" />
                    </div>
                    <div className="ml-3">
                        <h4 className="text-sm font-semibold text-blue-800 mb-1">
                            Important Template Limitation
                        </h4>
                        <p className="text-sm text-blue-700 leading-relaxed">
                            For campaigns, we only support variables in the <strong>BODY section</strong> of templates.
                            While <strong>IMAGE, VIDEO, and DOCUMENT</strong> headers work perfectly in campaigns,
                            <strong>TEXT headers with variables</strong> are not supported and may cause delivery failures.
                            Please select templates with appropriate header structure to ensure successful message delivery.
                        </p>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="p-4">
                <div className="space-y-6">
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                        <div>
                            <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                                Campaign Name *
                            </label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                required
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>

                        <div>
                            <label htmlFor="templateName" className="block text-sm font-medium text-gray-700">
                                Template *
                            </label>
                            <select
                                id="templateName"
                                name="templateName"
                                value={formData.templateName}
                                onChange={handleTemplateSelect}
                                required
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="">Select a template</option>
                                {templates.map(template => (
                                    <option key={template.id} value={template.name}>
                                        {template.name} ({template.language})
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div>
                        <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                            Description
                        </label>
                        <textarea
                            id="description"
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            rows={3}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>

                    <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                        <div>
                            <label htmlFor="settings.batchSize" className="block text-sm font-medium text-gray-700">
                                Batch Size
                            </label>
                            <input
                                type="number"
                                id="settings.batchSize"
                                name="settings.batchSize"
                                value={formData.settings.batchSize}
                                onChange={handleInputChange}
                                min={1}
                                max={1000}
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                            <p className="mt-1 text-xs text-gray-500">Messages per batch</p>
                        </div>

                        <div>
                            <label htmlFor="settings.delayBetweenMessages" className="block text-sm font-medium text-gray-700">
                                Message Delay (ms)
                            </label>
                            <input
                                type="number"
                                id="settings.delayBetweenMessages"
                                name="settings.delayBetweenMessages"
                                value={formData.settings.delayBetweenMessages}
                                onChange={handleInputChange}
                                min={0}
                                max={10000}
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                            <p className="mt-1 text-xs text-gray-500">Delay between each message</p>
                        </div>

                        <div>
                            <label htmlFor="settings.delayBetweenBatches" className="block text-sm font-medium text-gray-700">
                                Batch Delay (ms)
                            </label>
                            <input
                                type="number"
                                id="settings.delayBetweenBatches"
                                name="settings.delayBetweenBatches"
                                value={formData.settings.delayBetweenBatches}
                                onChange={handleInputChange}
                                min={0}
                                max={300000}
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                            <p className="mt-1 text-xs text-gray-500">Delay between each batch</p>
                        </div>
                    </div>

                    {selectedTemplate && (
                        <div className="border border-gray-200 rounded-md p-4">
                            <h3 className="text-md font-medium mb-4">Template Preview</h3>

                            {/* Template info */}
                            <div className="mb-4">
                                <p><strong>Name:</strong> {selectedTemplate.name}</p>
                                <p><strong>Category:</strong> {selectedTemplate.category}</p>
                                <p><strong>Language:</strong> {selectedTemplate.language}</p>
                            </div>

                            {/* Media header input (if template has media header) */}
                            {mediaHeaders.hasMedia && (
                                <div className="mb-4">
                                    <h4 className="text-sm font-medium mb-2">Media Header</h4>
                                    <div className="space-y-2">
                                        <div>
                                            <label className="block text-sm text-gray-700 mb-1">
                                                {mediaHeaders.type.charAt(0).toUpperCase() + mediaHeaders.type.slice(1)} URL
                                            </label>
                                            <input
                                                type="text"
                                                value={mediaHeaders.url}
                                                onChange={(e) => handleMediaHeaderChange(e.target.value)}
                                                placeholder={`Enter ${mediaHeaders.type} URL`}
                                                className="w-full p-2 border border-gray-300 rounded"
                                            />
                                            <p className="text-xs text-gray-500 mt-1">
                                                {mediaHeaders.type === 'image' && 'Enter a valid image URL (JPG, PNG, etc.)'}
                                                {mediaHeaders.type === 'video' && 'Enter a valid video URL (MP4, etc.)'}
                                                {mediaHeaders.type === 'document' && 'Enter a valid document URL (PDF, etc.)'}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Test variables */}
                            <div className="mb-4">
                                <h4 className="text-sm font-medium mb-2">Test Variables</h4>
                                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                                    {Object.keys(testVariables).map(varName => (
                                        <div key={varName}>
                                            <label htmlFor={`var_${varName}`} className="block text-sm font-medium text-gray-700">
                                                {varName}
                                            </label>
                                            <input
                                                type="text"
                                                id={`var_${varName}`}
                                                name={varName}
                                                value={testVariables[varName]}
                                                onChange={handleVariableChange}
                                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <button
                                type="button"
                                onClick={handleTestTemplate}
                                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
                            >
                                Test Template
                            </button>

                            {/* Render template preview */}
                            {renderTemplatePreview()}
                        </div>
                    )}

                    <div className="flex justify-end space-x-3">
                        <button
                            type="button"
                            onClick={handleCancel}
                            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            {loading ? 'Saving...' : (isEdit ? 'Update Campaign' : 'Create Campaign')}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default CampaignForm;